<script setup lang="ts">
import { ref, watch } from 'vue'
import { Heart, Star, Share2 } from 'lucide-vue-next'
import type { IRefVideoItem } from '../types'
import { showToast, showDialog } from 'vant'
import { useLikeStore } from '@/stores/like'
import { useCollectStore } from '@/stores/collect'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores/app'

const appStore = useAppStore()

type IProps = Pick<
  IRefVideoItem,
  'liked' | 'likeCount' | 'collected' | 'collectCount' | 'commented' | 'commentCount' | 'shareCount'
> & {
  episodeId: number
  scriptId: number
}

const props = defineProps<IProps>()

const likeStore = useLikeStore()
const collectStore = useCollectStore()
const userStore = useUserStore()
const router = useRouter()

// 使用本地响应式状态，同步 props 和 store 状态
const liked = ref(props.liked)
const likeCount = ref(props.likeCount)
const collected = ref(props.collected)
const collectCount = ref(props.collectCount)

// 监听 props 变化，更新本地状态
watch(
  () => props.liked,
  val => {
    liked.value = val
  },
)
watch(
  () => props.likeCount,
  val => {
    likeCount.value = val
  },
)
watch(
  () => props.collected,
  val => {
    collected.value = val
  },
)
watch(
  () => props.collectCount,
  val => {
    collectCount.value = val
  },
)

// 监听 store 状态变化（乐观更新后同步）
watch(
  () => likeStore.isLiked(props.episodeId),
  val => {
    liked.value = val
  },
)
watch(
  () => collectStore.isCollected(props.episodeId),
  val => {
    collected.value = val
  },
)

/**
 * 处理点赞
 */
const handleLike = async () => {
  // 检查登录状态
  if (!userStore.isLogin) {
    showDialog({
      title: '提示',
      message: '请先登录后再点赞',
      confirmButtonText: '去登录',
      cancelButtonText: '取消',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-purple-600)',
    }).then(action => {
      if (action === 'confirm') {
        router.push(`/login?redirect=${encodeURIComponent(router.currentRoute.value.fullPath)}`)
      }
    })
    return
  }

  // 乐观更新 UI
  const prevLiked = liked.value
  const willBeLiked = !prevLiked
  liked.value = willBeLiked
  likeCount.value = willBeLiked ? likeCount.value + 1 : Math.max(0, likeCount.value - 1)

  // 调用 store 方法
  const success = await likeStore.toggleLike(props.episodeId, props.scriptId, prevLiked)

  // 如果失败，回滚 UI
  if (!success) {
    liked.value = !willBeLiked
    likeCount.value = willBeLiked ? Math.max(0, likeCount.value - 1) : likeCount.value + 1
  }
}

/**
 * 处理收藏
 */
const handleCollect = async () => {
  // 检查登录状态
  if (!userStore.isLogin) {
    showDialog({
      title: '提示',
      message: '请先登录后再收藏',
      confirmButtonText: '去登录',
      cancelButtonText: '取消',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-purple-600)',
    }).then(action => {
      if (action === 'confirm') {
        router.push(`/login?redirect=${encodeURIComponent(router.currentRoute.value.fullPath)}`)
      }
    })
    return
  }

  // 乐观更新 UI
  const prevCollected = collected.value
  const willBeCollected = !prevCollected
  collected.value = willBeCollected
  collectCount.value = willBeCollected ? collectCount.value + 1 : Math.max(0, collectCount.value - 1)

  // 调用 store 方法
  const success = await collectStore.toggleCollect(props.episodeId, props.scriptId, prevCollected)

  // 如果失败，回滚 UI
  if (!success) {
    collected.value = !willBeCollected
    collectCount.value = willBeCollected ? Math.max(0, collectCount.value - 1) : collectCount.value + 1
  }
}

/**
 * 分享
 */
const share = () => showToast('功能火速开发中~')

// 格式化数字（如 1.2k, 3.5w）
const formatNumber = (count?: number): string => {
  if (!count) return '0'
  if (count < 1000) return String(count)
  if (count < 10000) return (count / 1000).toFixed(1) + 'k'
  return (count / 10000).toFixed(1) + 'w'
}
</script>

<template>
  <div class="absolute right-4 bottom-16 flex flex-col gap-6 pointer-events-auto z-100">
    <!-- 点赞 -->
    <div
      @click.stop="handleLike"
      class="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform cursor-pointer"
    >
      <Heart :class="[liked ? 'fill-red-600 text-red-600' : 'fill-white text-white']" class="w-8 h-8" />
      <div class="text-xs">{{ formatNumber(likeCount) }}</div>
    </div>

    <!-- 评论（暂不支持） -->
    <div
      @click.stop="appStore.toggleShowComments(props.scriptId)"
      class="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform cursor-pointer"
    >
      <!-- <MessageCircleMore class="w-8 h-8 fill-white stroke-black" /> -->
      <van-icon name="chat" :size="34" />
      <div class="text-xs">{{ formatNumber(commentCount) }}</div>
    </div>

    <!-- 收藏 -->
    <div
      @click.stop="handleCollect"
      class="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform cursor-pointer"
    >
      <Star :class="[collected ? 'fill-yellow-400 text-yellow-400' : 'fill-white text-white']" class="w-8 h-8" />
      <div class="text-xs">{{ formatNumber(collectCount) }}</div>
    </div>

    <!-- 分享 -->
    <div
      @click.stop="share"
      class="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform cursor-pointer"
    >
      <Share2 class="w-8 h-8 fill-white" />
      <div class="text-xs">{{ formatNumber(shareCount) }}</div>
    </div>
  </div>
</template>
